import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
  ClipboardEvent,
} from 'react'
import cn from 'classnames'

import { useTranslation } from 'utils/use-translation'
import { Captcha } from 'components/app/captcha'
import Loader from 'components/ui/loader'
import useViewport from 'libs/use-viewport'

import styles from './styles.module.scss'

interface CodeVerificationInputProps {
  length?: number
  code: string
  setCode: (val: string) => void
  isCodeValid: boolean
  setIsCodeValid: Dispatch<SetStateAction<boolean>>
  errorMsg: string
  setErrorMsg: Dispatch<SetStateAction<string>>
  countSeconds?: number
  isLoading?: boolean
  resendBtn?: boolean
  inputClassName?: string
  onResendCode?: (captcha: string) => Promise<void>
  disabled?: boolean
  verifyCode: (code: string) => Promise<void>
  showError?: boolean
  resendWithCaptcha?: boolean
  withFooter?: boolean
  formClassName?: string
  wrapperClassName?: string
}

export const CodeVerificationInput = ({
  length = 6,
  code,
  setCode,
  isCodeValid,
  setIsCodeValid,
  errorMsg,
  setErrorMsg,
  countSeconds: initialCount,
  resendBtn = false,
  inputClassName,
  onResendCode,
  disabled = false,
  verifyCode,
  showError = false,
  resendWithCaptcha = false,
  isLoading = false,
  withFooter = true,
  formClassName = '',
  wrapperClassName = '',
}: CodeVerificationInputProps) => {
  const { t } = useTranslation()
  const view = useViewport()
  const isMobile = view.includes('mobile') || view.includes('xs-mobile')

  const [countSeconds, setCountSeconds] = useState(initialCount || 0)
  const resendTimerRef = useRef<any>(null)
  const inputsRef = useRef<HTMLInputElement[]>([])
  const [isFocused, setIsFocused] = useState(false)

  const handleFocus = () => setIsFocused(true)
  const handleBlur = () => setIsFocused(false)

  useEffect(() => {
    if (disabled || countSeconds <= 0) return undefined

    resendTimerRef.current = setInterval(() => {
      setCountSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(resendTimerRef.current)
  }, [disabled, countSeconds])

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const val = e.target.value.replace(/\D/g, '')
    if (!val) return

    const newCode = code.split('')
    newCode[index] = val
    const updatedCode = newCode.join('')
    setCode(updatedCode)

    if (index < length - 1) {
      inputsRef.current[index + 1]?.focus()
    }

    if (updatedCode.length === length) {
      verifyCode(updatedCode)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        const newCode = code.split('')
        newCode[index - 1] = ''
        setCode(newCode.join(''))
        inputsRef.current[index - 1]?.focus()
      } else {
        const newCode = code.split('')
        newCode[index] = ''
        setCode(newCode.join(''))
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handleResend = async (captcha: string) => {
    if (disabled || countSeconds > 0 || !onResendCode) return
    try {
      await onResendCode(captcha)
      clearInterval(resendTimerRef.current)
      setCountSeconds(initialCount)
      inputsRef.current[0]?.focus()
    } catch {}
  }

  const handleWrapperClick = (e) => {
    const wrapperRect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - wrapperRect.left

    let closestIndex = 0
    let closestDistance = Infinity

    inputsRef.current.forEach((input, index) => {
      if (!input) return
      const inputRect = input.getBoundingClientRect()
      const inputX = inputRect.left - wrapperRect.left + inputRect.width / 2
      const distance = Math.abs(clickX - inputX)
      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = index
      }
    })

    inputsRef.current[closestIndex]?.focus()
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)

    if (!pasteData) return

    const newCodeArray = code.split('')
    for (let i = 0; i < pasteData.length; i++) {
      newCodeArray[i] = pasteData[i]
    }

    const updatedCode = newCodeArray.join('')
    setCode(updatedCode)

    const nextIndex = pasteData.length < length ? pasteData.length : length - 1
    inputsRef.current[nextIndex]?.focus()

    if (updatedCode.length === length) {
      verifyCode(updatedCode)
    }
  }

  useEffect(() => {
    if (!code.length) inputsRef.current[0]?.focus()

    setIsCodeValid(true)
    setErrorMsg('')
  }, [code])

  useEffect(() => {
    setCode('')
    setIsCodeValid(true)
    setErrorMsg('')
  }, [])

  const isDisabled = countSeconds > 0
  const countdownText = `${t('resend_in')} ${countSeconds} ${t('seconds')}`
  const resendText = t('resend_code')

  const getResendButton = () => (
    <span className={styles['btn-wrapper__resend']}>{isDisabled ? countdownText : resendText}</span>
  )

  return (
    <form className={cn(styles.form, formClassName)}>
      <div className={cn(styles.wrapper, wrapperClassName)} onClick={handleWrapperClick}>
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el!)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={code[i] || ''}
            onChange={(e) => handleChange(e, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onPaste={handlePaste}
            className={cn(styles.wrapper__input, {
              [styles.wrapper__err]: (!isCodeValid || errorMsg) && code.length === length,
              [styles.active]: code.length === i,
              [inputClassName]: !!inputClassName,
            })}
            placeholder={!isFocused ? '0' : ''}
          />
        ))}

        {isLoading && withFooter && !isMobile && <Loader className={styles.loader} size={30} />}
      </div>
      {withFooter && (
        <>
          <div className={styles.footer}>
            {showError && errorMsg && <small className={styles.error}>{errorMsg}</small>}

            {resendWithCaptcha ? (
              isDisabled ? (
                getResendButton()
              ) : (
                <Captcha onCaptchaSuccess={handleResend}>
                  <button
                    type="button"
                    disabled={isDisabled}
                    className={cn(styles['btn-wrapper'], {
                      [styles.disabled]: isDisabled,
                      [styles.hidden]: !resendBtn,
                    })}
                  >
                    {getResendButton()}
                  </button>
                </Captcha>
              )
            ) : (
              <button
                type="button"
                disabled={isDisabled}
                onClick={() => handleResend('')}
                className={cn(styles['btn-wrapper'], {
                  [styles.disabled]: isDisabled,
                  [styles.hidden]: !resendBtn,
                })}
              >
                {getResendButton()}
              </button>
            )}
          </div>

          {isLoading && isMobile && <Loader className={styles.loader} size={30} />}
        </>
      )}
    </form>
  )
}

export default CodeVerificationInput
